import { computed, DestroyRef, ErrorHandler, Injectable, Signal, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { LoadingService } from '@coin/shared/core/data-access';
import { Employee, EmptyGuid, PaginatedResult } from '@coin/shared/core/util-models';
import { produce } from 'immer';
import { defer, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { catchError, defaultIfEmpty, filter, finalize, first, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { FilterSortState } from '@coin/customer/shared/filter-sort-bar-data-access';
import { OrgTreeSortType } from '../../../shared/enums/org-tree-sorting.enum';
import { IncentiveAllocationEmployee } from '../../merit-shared/models/incentive-allocation-employee.model';
import { MeritAllocationOrganisationSnapshot } from '../../merit-shared/models/merit-allocation-organisation-snapshot.model';
import { EmployeeWithCompensationInfo } from '../../merit-shared/models/merit-budget-direct-with-compensation.model';
import { AllocationOrgDetailsMappingService } from '../../merit-shared/services/allocation-org-details-mapping.service';
import { AllocationUserOrgCodeService } from '../../merit-shared/services/allocation-user-org-code.service';
import { MeritAllocationImpersonatedEmployeeService } from '../../merit-shared/services/merit-allocation-impersonated-employee.service';
import { MeritAllocationOrganisationsService } from '../../merit-shared/services/merit-allocation-organisations.service';
import { MeritBudgetAllocationState } from './merit-budget-allocation.state';

const MAX_DEPTH_LEVEL = 10;
const DEFAULT_ID = EmptyGuid;
const INITIAL_PAGE = 1;

export interface EmployeeAllocation
  extends Partial<EmployeeWithCompensationInfo>, Partial<IncentiveAllocationEmployee> {}

export interface AllocationOrgDetailsNode extends EmployeeAllocation {
  parentOrgCode: string;
  childNodes: string[];
  isHead: boolean;
}

interface SearchedNode {
  node: Employee;
  isActive: boolean;
}

export interface BreadcrumbItem extends EmployeeWithCompensationInfo {
  childrenActive: boolean;
  disabledDepth?: boolean;
  isRoot: boolean;
}

export interface MeritBudgetAllocationTotalStateModel {
  nodes: AllocationOrgDetailsNode[];
  breadcrumbItems: BreadcrumbItem[];
  areEmployeesLoading: boolean;
  searchedNode: SearchedNode;
  sortingType: OrgTreeSortType;
  currentLevel: number;
  userOrganisations: MeritAllocationOrganisationSnapshot[];
  selectedOrganisation: MeritAllocationOrganisationSnapshot;
  page: number;
  maxPage: number;
}

// Auto-migrated from NGXS
@Injectable({ providedIn: 'root' })
export class MeritBudgetAllocationTotalState {
  private readonly meritAllocationOrganisationsService = inject(MeritAllocationOrganisationsService);
  private readonly allocationOrgDetailsMappingService = inject(AllocationOrgDetailsMappingService);
  private readonly filterSortState = inject(FilterSortState);
  private readonly loadingService = inject(LoadingService);
  private readonly allocationUserOrgCodeService = inject(AllocationUserOrgCodeService);
  private readonly meritAllocationImpersonatedEmployeeService = inject(MeritAllocationImpersonatedEmployeeService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly meritBudgetAllocationState = inject(MeritBudgetAllocationState);

  public loadAllocationMembersSuccessful$ = new Subject<void>();
  public loadUserHeadOrganisationsSuccessful$ = new Subject<void>();

  private readonly meritBudgetAllocationTotalStateModel = signal<MeritBudgetAllocationTotalStateModel>({
    nodes: [],
    breadcrumbItems: [],
    areEmployeesLoading: false,
    sortingType: OrgTreeSortType.Default,
    searchedNode: { node: null, isActive: false },
    currentLevel: 1,
    userOrganisations: [],
    selectedOrganisation: null,
    page: INITIAL_PAGE,
    maxPage: INITIAL_PAGE
  });

  public readonly nodes = computed(() => this.meritBudgetAllocationTotalStateModel().nodes);
  public breadcrumbItems = computed(() => this.meritBudgetAllocationTotalStateModel().breadcrumbItems);
  public areEmployeesLoading = computed(() => this.meritBudgetAllocationTotalStateModel().areEmployeesLoading);
  public currentLevel = computed(() => this.meritBudgetAllocationTotalStateModel().currentLevel);
  public userOrganisations = computed(() => this.meritBudgetAllocationTotalStateModel().userOrganisations);
  public selectedOrganisation = computed(() => this.meritBudgetAllocationTotalStateModel().selectedOrganisation);
  public selectedOrganisationOrgCode = computed(
    () => this.meritBudgetAllocationTotalStateModel().selectedOrganisation?.orgCode
  );
  public isHeadOfHeads = computed(() =>
    this.meritBudgetAllocationTotalStateModel().userOrganisations?.some(org => org.isParentOrganisation)
  );

  public getNodesByLastBCItem = computed(() => {
    const state = this.meritBudgetAllocationTotalStateModel();
    const id = state.breadcrumbItems?.[state.breadcrumbItems.length - 1]?.id;
    const parent = state.nodes?.find(node => node.displayId === id);
    return parent?.childNodes?.map(id => state.nodes.find(node => node.displayId === id));
  });

  public getNodesByParentId(id: string): Signal<AllocationOrgDetailsNode[]> {
    return computed(() => {
      const state = this.meritBudgetAllocationTotalStateModel();
      const parent = state.nodes?.find(node => node.displayId === id);
      return parent?.childNodes?.map(id => state.nodes.find(node => node.displayId === id));
    });
  }

  public hasSubHeadNodes(id: string): Signal<boolean> {
    return computed(() => {
      const state = this.meritBudgetAllocationTotalStateModel();
      const parent = state.nodes?.find(node => node.displayId === id);
      return parent?.childNodes?.map(id => state.nodes.find(node => node.displayId === id)).some(node => node?.isHead);
    });
  }

  public node(id: string): Signal<AllocationOrgDetailsNode> {
    return computed(() => this.meritBudgetAllocationTotalStateModel().nodes?.find(node => node.displayId === id));
  }

  public nodeByHead(headId: string, orgCode: string): Signal<AllocationOrgDetailsNode> {
    return computed(() =>
      this.meritBudgetAllocationTotalStateModel().nodes?.find(
        node => node.headId === headId && node.orgCode === orgCode
      )
    );
  }

  public employeeNode(employeeId: string): Signal<AllocationOrgDetailsNode> {
    return computed(() => {
      const state = this.meritBudgetAllocationTotalStateModel();
      // a missing selected organisation threw here, which NGXS suppressErrors turned into `undefined`
      const isLoading =
        !state.nodes?.length ||
        (state.nodes.length === 1 &&
          (!state.selectedOrganisation || state.nodes[0].id === state.selectedOrganisation.headId));
      if (isLoading) {
        return undefined;
      }
      return state.nodes?.find(node => node.employeeId === employeeId) ?? null;
    });
  }

  updateSortingType(sortingType: OrgTreeSortType): void {
    this.meritBudgetAllocationTotalStateModel.update(state =>
      produce(state, draft => {
        draft.sortingType = sortingType;
      })
    );
  }

  loadAllocationMembers(payload: { loadRoot: boolean; reload: boolean }): Observable<string | void> {
    // ngxs compatibility: the empty-page path completes without a value but was still successful
    return defer(() => {
      let hasValue = false;
      return this.startAllocationMembersLoad(payload).pipe(
        tap({
          next: () => {
            hasValue = true;
            this.loadAllocationMembersSuccessful$.next();
          },
          complete: () => {
            if (!hasValue) {
              this.loadAllocationMembersSuccessful$.next();
            }
          }
        })
      );
    });
  }

  private startAllocationMembersLoad(payload: { loadRoot: boolean; reload: boolean }): Observable<string | void> {
    let node: Partial<AllocationOrgDetailsNode>;
    if (payload.loadRoot) {
      node = { id: 'root', displayId: 'root', paOrgCode: 'root' };
    } else {
      const state = this.meritBudgetAllocationTotalStateModel();
      const id = state.breadcrumbItems[state.breadcrumbItems.length - 1]?.id;
      node = state.nodes?.find(node => node.displayId === id);
    }

    if (this.meritBudgetAllocationTotalStateModel().nodes?.some(node => node.displayId === 'root')) {
      return of('');
    }

    this.meritBudgetAllocationTotalStateModel.update(state =>
      produce(state, draft => {
        this.updateNodes(draft.nodes, { ...node });
        const index = draft.nodes.findIndex(e => e.displayId === node.displayId);
        draft.nodes[index].childNodes = [];
      })
    );

    return this.loadAllocationMemberChildren({
      node,
      reload: payload.reload,
      resetSearchNode: true,
      isFirstPageReload: true
    }).pipe(map(() => undefined));
  }

  lazyLoadAllocationMembers(): Observable<void> {
    const state = this.meritBudgetAllocationTotalStateModel();
    const id = state.breadcrumbItems[state.breadcrumbItems.length - 1]?.id;
    const node = state.nodes?.find(node => node.displayId === id);

    return this.loadAllocationMemberChildren({
      node,
      reload: true,
      resetSearchNode: true,
      isFirstPageReload: false
    }).pipe(map(() => undefined));
  }

  loadAllocationMemberChildren(payload: {
    node: EmployeeAllocation;
    isFirstPageReload: boolean;
    reload?: boolean;
    resetSearchNode?: boolean;
    disableNavigation?: boolean;
    isBudgetView?: boolean;
  }): Observable<string | void | PaginatedResult<EmployeeWithCompensationInfo | IncentiveAllocationEmployee>> {
    if (!payload.reload && !payload.disableNavigation) {
      const childNodes = this.getNodesByParentId(payload?.node?.displayId)();
      if (childNodes?.length) {
        // do not fire req if childNodes already existent, but update breadcrumb
        return !payload.isBudgetView || childNodes.some(childNode => childNode?.isHead)
          ? of(undefined).pipe(tap(() => this.updateBreadcrumbItems({ id: payload.node.displayId })))
          : of(null);
      }
    }

    if (payload.resetSearchNode && !payload.disableNavigation) {
      this.updateSearchedNode({ node: null, isActive: false });
    }

    const orgCode = payload?.node?.headId || payload?.node?.isHead ? payload.node?.orgCode : payload.node?.paOrgCode;
    const seasonId = this.meritBudgetAllocationState.season().id;
    const filterText = !payload.disableNavigation ? this.filterSortState.getSortAndFilterText() : '';
    const page = payload.isFirstPageReload ? INITIAL_PAGE : this.meritBudgetAllocationTotalStateModel().page;

    if (page > this.meritBudgetAllocationTotalStateModel().maxPage && !payload.isFirstPageReload) {
      return of('');
    }

    if (!orgCode || !seasonId) {
      return of(null);
    }

    return this.allocationOrgDetailsMappingService.service
      .getTotalAllocations(page, 25, seasonId, `&parentOrgCode=${encodeURIComponent(orgCode)}${filterText ?? ''}`)
      .pipe(
        filter(data => !!data?.content?.length),
        mergeMap((data: PaginatedResult<EmployeeWithCompensationInfo | IncentiveAllocationEmployee>) => {
          this.meritBudgetAllocationTotalStateModel.update(state =>
            produce(state, draft => {
              draft.page = payload.isFirstPageReload ? INITIAL_PAGE + 1 : draft.page + 1;
              draft.maxPage = payload.isFirstPageReload ? data.pageCount : draft.maxPage;
            })
          );
          const members = data.content?.filter(node => payload.node.headId !== node.id);

          this.loadAllocationMemberChildrenSuccess({
            children: members,
            id: payload.node.displayId,
            reload: payload.reload,
            disableNavigation: payload.disableNavigation,
            isBudgetView: payload.isBudgetView
          });
          return of(undefined);
        }),
        finalize(() =>
          this.meritBudgetAllocationTotalStateModel.update(state => ({ ...state, areEmployeesLoading: false }))
        )
      );
  }

  reloadAllocationMemberChild(id: string): Observable<unknown> {
    const seasonId = this.meritBudgetAllocationState.season().id;

    return this.allocationOrgDetailsMappingService.service.getAllocationMember(seasonId, id).pipe(
      map(node =>
        this.meritBudgetAllocationTotalStateModel.update(state =>
          produce(state, draft => {
            if (node) {
              const index = draft.nodes.findIndex(stateNode => stateNode.displayId === node?.displayId);
              if (index > -1) {
                draft.nodes[index] = { ...draft.nodes[index], ...node };
              } else {
                draft.nodes.push(node as AllocationOrgDetailsNode);
              }
            }
          })
        )
      )
    );
  }

  loadAllocationMemberChildrenSuccess(payload: {
    children: (EmployeeWithCompensationInfo | IncentiveAllocationEmployee)[];
    id: string;
    reload: boolean;
    disableNavigation: boolean;
    isBudgetView: boolean;
  }): void {
    this.meritBudgetAllocationTotalStateModel.update(state =>
      produce(state, draft => {
        if (!payload.children) {
          return;
        }

        for (const child of payload.children) {
          this.updateNodes(draft.nodes, { ...child, ...{ parentId: payload.id } });
        }

        const nodeIndex = draft.nodes.findIndex(node => node.displayId === payload.id);

        if (nodeIndex === -1) {
          return;
        }

        const nodes = draft.nodes[nodeIndex].childNodes || [];
        draft.nodes[nodeIndex].childNodes = [
          ...new Set([...nodes, ...payload.children.map(child => child?.displayId)])
        ];
      })
    );

    if (payload.disableNavigation || (payload.isBudgetView && !payload.children.some(childNode => childNode?.isHead))) {
      return;
    }

    this.updateBreadcrumbItems({ id: payload.id });
  }

  async openEmployeeInView(payload: { node: Employee; fullTree: boolean }): Promise<void> {
    try {
      this.loadingService.present();

      let parents: EmployeeAllocation[] = [];

      await this.collectAllParents(
        this.meritBudgetAllocationTotalStateModel().selectedOrganisation?.orgCode,
        payload.node,
        parents,
        true,
        payload.fullTree
      );

      if (payload.node?.id === parents[0]?.headId && parents?.length > 1 && payload?.fullTree) {
        parents.shift();
      }

      this.resetChildrenVisibility().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      const breadcrumbPayloads: { id: string }[] = [];

      const newHeadInView: EmployeeAllocation = parents[0];
      parents = parents.filter(parent => parent.paOrgCode !== 'root')?.reverse();

      parents.forEach(parent => {
        this.meritBudgetAllocationTotalStateModel.update(state =>
          produce(state, draft => {
            this.updateNodes(draft.nodes, { ...parent });
          })
        );
        breadcrumbPayloads.push({ id: parent.displayId });
      });

      for (const [index, breadcrumbPayload] of breadcrumbPayloads.entries()) {
        setTimeout(() => {
          this.updateBreadcrumbItems(breadcrumbPayload);
        }, index * 200);
      }

      setTimeout(() => {
        this.loadAllocationMemberChildren({
          node: newHeadInView,
          reload: true,
          resetSearchNode: false,
          isFirstPageReload: true
        })
          // ngxs compatibility: the dispatch emitted once when the action completed, even without a value
          .pipe(defaultIfEmpty(undefined), takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.updateSearchedNode({ node: payload.node, isActive: true });
          });
      }, 200);
    } catch (e) {
      throw new Error(e);
    } finally {
      this.loadingService.dismiss();
    }
  }

  updateBreadcrumbItems(payload?: { id?: string }): void {
    // case for reloading root hubs
    if (payload?.id === null) {
      payload.id = 'root';
    }

    this.meritBudgetAllocationTotalStateModel.update(state =>
      produce(state, draft => {
        if (!payload) {
          // reset items
          draft.breadcrumbItems.splice(1, draft.breadcrumbItems.length);
          return;
        }

        if (draft.breadcrumbItems.some(item => item.id === payload.id)) {
          // update item

          let pos: number;
          for (let i = 0; i < draft.breadcrumbItems.length; i++) {
            if (draft.breadcrumbItems[i].id === payload.id) {
              pos = i;
              draft.breadcrumbItems[i].version += 1;
            }
          }
          if (payload.id !== 'root') {
            draft.breadcrumbItems.splice(pos + 1, draft.breadcrumbItems.length); // delete unnecessary breadcrumb items
          }
        } else if (draft.breadcrumbItems.length === MAX_DEPTH_LEVEL) {
          draft.breadcrumbItems.push({
            id: payload.id,
            childrenActive: true,
            disabledDepth: true,
            isRoot: payload.id === 'root',
            version: 0
          });
        } else {
          draft.breadcrumbItems.push({
            id: payload.id,
            childrenActive: true,
            isRoot: payload.id === 'root',
            version: 0
          });
        }
      })
    );
  }

  resetChildrenVisibility(): Observable<void> {
    return of(undefined).pipe(tap(() => this.updateBreadcrumbItems()));
  }

  setSelectedOrganisation(selectedOrganisation: MeritAllocationOrganisationSnapshot): void {
    this.meritBudgetAllocationTotalStateModel.update(state =>
      produce(state, draft => {
        draft.selectedOrganisation = selectedOrganisation;
      })
    );
  }

  updateSearchedNode(payload: { node: Employee; isActive: boolean }): void {
    this.meritBudgetAllocationTotalStateModel.update(state =>
      produce(state, draft => {
        draft.searchedNode.node = payload.node;
        draft.searchedNode.isActive = payload.isActive;
      })
    );
  }

  setCurrentLevel(payload: { level: number }): void {
    this.meritBudgetAllocationTotalStateModel.update(state =>
      produce(state, draft => {
        draft.currentLevel = payload.level;
      })
    );
  }

  updateCurrentLevel(payload: { levelDelta: number }): void {
    this.meritBudgetAllocationTotalStateModel.update(state =>
      produce(state, draft => {
        draft.currentLevel += payload.levelDelta;
      })
    );
  }

  updateNode(payload?: unknown): void {
    this.meritBudgetAllocationTotalStateModel.update(state =>
      produce(state, draft => {
        this.updateNodes(draft.nodes, payload);
      })
    );
  }

  loadUserHeadOrganisations(): Observable<[MeritAllocationOrganisationSnapshot[], string]> {
    const seasonId = this.meritBudgetAllocationState.season()?.id;

    this.meritBudgetAllocationTotalStateModel.update(state => ({ ...state, areEmployeesLoading: true }));
    return this.meritAllocationImpersonatedEmployeeService.getGid().pipe(
      switchMap(gid =>
        this.meritAllocationOrganisationsService.getOrganisations(seasonId, `&query.headGid=${encodeURIComponent(gid)}`)
      ),
      switchMap(orgs =>
        this.allocationUserOrgCodeService
          .getUserOrgCode()
          .pipe(map(orgCode => [orgs, orgCode] as [MeritAllocationOrganisationSnapshot[], string]))
      ),
      tap(([orgs, userOrgCode]) => {
        this.meritBudgetAllocationTotalStateModel.update(state =>
          produce(state, draft => {
            const headOfHeadsOrgs = orgs.filter(org => org.isParentOrganisation);
            if (headOfHeadsOrgs?.length) {
              const organizationInView = headOfHeadsOrgs.find(org => org.orgCode === userOrgCode) ?? headOfHeadsOrgs[0];
              draft.userOrganisations = orgs;
              draft.selectedOrganisation = organizationInView;
              draft.nodes = [];
              draft.breadcrumbItems = [];
              draft.searchedNode = { node: null, isActive: false };
            } else {
              draft.userOrganisations = orgs;
            }
          })
        );
      }),
      // ngxs compatibility: the action was successful once the organisations were stored, the open-in-view step ran detached
      tap(() => this.loadUserHeadOrganisationsSuccessful$.next()),
      switchMap(result =>
        (this.isHeadOfHeads() ? this.openSelectedOrganisationInView(seasonId) : of(undefined)).pipe(map(() => result))
      )
    );
  }

  // formerly a detached async function: its failure was only reported and did not fail the head organisation load
  private openSelectedOrganisationInView(seasonId: string): Observable<void> {
    const selectedOrg = this.meritBudgetAllocationTotalStateModel().selectedOrganisation;

    return this.allocationOrgDetailsMappingService.service
      .getAllocationMember(seasonId, selectedOrg.headId, selectedOrg.orgCode)
      .pipe(
        first(),
        map((allocationMember: EmployeeWithCompensationInfo) => {
          // Ensure the OrgCode and ParentOrgCode is taken from the AllocationManager instead of from the Employee
          const am = {
            ...allocationMember,
            ...allocationMember.employeeSnapshot,
            orgCode: allocationMember.orgCode,
            paOrgCode: allocationMember.paOrgCode ?? allocationMember.orgCode
          };

          this.openEmployeeInView({ node: am, fullTree: false });
          this.setCurrentLevel({ level: 1 });
        }),
        catchError(error => {
          this.errorHandler.handleError(error);
          return of(undefined);
        })
      );
  }

  updateNodeCompensationComponents(payload: EmployeeWithCompensationInfo): void {
    this.meritBudgetAllocationTotalStateModel.update(state =>
      produce(state, draft => {
        const foundIndex = draft.nodes.findIndex(node => node.employeeId === payload.employeeId);

        if (foundIndex >= 0) {
          const node = draft.nodes[foundIndex];
          node.compensationComponents = payload.compensationComponents ?? node?.compensationComponents;
          node.isDone = payload.isDone ?? node?.isDone;
          node.promotion = payload.promotion ?? node?.promotion;
        }
      })
    );
  }

  updateNodes(nodes: Partial<AllocationOrgDetailsNode>[], updatedNode: Partial<AllocationOrgDetailsNode>): void {
    // check if node already exists
    if (nodes.find(node => node.displayId === updatedNode.displayId && node.paOrgCode === updatedNode.paOrgCode)) {
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].displayId === updatedNode.displayId) {
          nodes[i] = { ...nodes[i], ...updatedNode };
        }
      }
    } else {
      nodes.push(updatedNode);
    }
  }

  async collectAllParents(
    selectedOrgCode: string,
    employee: Employee,
    parents: EmployeeAllocation[],
    isInital: boolean,
    isFullTree: boolean
  ): Promise<void> {
    const seasonId = this.meritBudgetAllocationState.season().id;
    const orgCode = employee?.headId || employee?.isHead ? employee?.orgCode : employee?.paOrgCode;

    let parentHead;

    if (orgCode) {
      if (isInital) {
        const orgs = await firstValueFrom(
          this.meritAllocationOrganisationsService.getOrganisations(
            seasonId,
            `&query.orgCode=${encodeURIComponent(orgCode)}`
          )
        );
        parentHead = orgs?.[0];
      } else {
        parentHead = await firstValueFrom(
          this.meritAllocationOrganisationsService.getParentOrganisation(seasonId, orgCode)
        );
      }
      if (parentHead.headId && parentHead.headId !== DEFAULT_ID) {
        parentHead.placeOfAction = parentHead.headPlaceOfAction; // hack
        parentHead.paOrgCode = parentHead.headPAOrgCode; // hack
      }

      const headAm: EmployeeWithCompensationInfo = await firstValueFrom(
        this.allocationOrgDetailsMappingService.service.getAllocationMember(
          seasonId,
          parentHead?.headId,
          parentHead?.orgCode
        )
      );
      if (headAm) {
        const { employeeSnapshot } = headAm;
        headAm.firstname = headAm.firstname || employeeSnapshot?.firstname;
        headAm.lastname = headAm.lastname || employeeSnapshot?.lastname;
        headAm.placeOfAction = headAm.placeOfAction || employeeSnapshot?.placeOfAction;
        headAm.gid = headAm.gid || employeeSnapshot?.gid;
        parents.push(headAm);
      } else {
        parents.push(parentHead);
      }

      if (parentHead.orgCode !== selectedOrgCode && isFullTree) {
        await this.collectAllParents(selectedOrgCode, parentHead, parents, false, true);
      }
    }
  }

  resetState(): void {
    this.meritBudgetAllocationTotalStateModel.update(state =>
      produce(state, draft => {
        draft.nodes = [];
        draft.breadcrumbItems = [];
        draft.sortingType = OrgTreeSortType.Default;
      })
    );
  }
}
